import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useScannerContext } from "@/contexts/ScannerContext";
import { useNFCReader } from "@/hooks/useNFCReader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, getCurrentUser } from "@/lib/apiClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Nfc, ScanLine } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface WorkOrder {
  id: string;
  order_number: string;
  product_name: string;
  customer?: string;
  quantity: number;
  priority: string;
  status: string;
  project_id?: string;
  custom_project_name?: string;
  mold_id?: string;
  part_id?: string;
  plybook_id?: string;
  scheduled_date?: string | null;
  planning_status?: string | null;
}

interface EditWorkOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workOrder: WorkOrder;
  onSuccess: () => void;
}

export function EditWorkOrderDialog({
  open,
  onOpenChange,
  workOrder,
  onSuccess,
}: EditWorkOrderDialogProps) {
  const { user, userRole } = useAuth();
  const { nfcBridge } = useScannerContext();
  // La fecha de inicio solo la pueden modificar supervisor y administrador
  const canEditStartDate = ["superadmin", "admin", "supervisor"].includes(userRole || "");
  const { isSupported: isMobileNfcSupported, scanTagUid, isScanning: isMobileScanning } = useNFCReader();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [scanningMobile, setScanningMobile] = useState(false);

  const [formData, setFormData] = useState({
    product_name: "",
    customer: "",
    quantity: 1,
    priority: "normal",
    project_id: "",
    custom_project_name: "",
    mold_id: "",
    part_id: "",
    plybook_id: "",
    nfc_tag_uid: "",
    scheduled_date: "",
  });

  // Load customers
  const { data: customers } = useQuery({
    queryKey: ["customers-active"],
    queryFn: async () => {
      const { data, error } = await api
        .from("customers")
        .select("id,name,code")
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  // Load projects
  const { data: projects } = useQuery({
    queryKey: ["projects-active"],
    queryFn: async () => {
      const { data, error } = await api
        .from("projects")
        .select("id,name,reference_code")
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  // Load molds
  const { data: molds } = useQuery({
    queryKey: ["molds-active"],
    queryFn: async () => {
      const { data, error } = await api
        .from("molds")
        .select("id,name,code")
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  // Load active plybooks
  const { data: plybooks } = useQuery({
    queryKey: ["active-plybooks"],
    queryFn: async () => {
      const { data, error } = await api
        .from("plybooks")
        .select("id, name, code, total_plies, status")
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  // Load existing NFC tag for this work order
  const { data: existingNfcTag, refetch: refetchNfcTag } = useQuery({
    queryKey: ["nfc-tag-work-order", workOrder?.id],
    queryFn: async () => {
      if (!workOrder?.id) return null;
      const { data, error } = await api
        .from("nfc_tags")
        .select("tag_uid, tag_label")
        .eq("work_order_id", workOrder.id)
        .maybeSingleRow();
      if (error) throw error;
      return data;
    },
    enabled: !!workOrder?.id && open,
  });

  // Initialize form when dialog opens
  useEffect(() => {
    if (open && workOrder) {
      setFormData({
        product_name: workOrder.product_name || "",
        customer: workOrder.customer || "",
        quantity: workOrder.quantity || 1,
        priority: workOrder.priority || "normal",
        project_id: workOrder.project_id || "",
        custom_project_name: workOrder.custom_project_name || "",
        mold_id: workOrder.mold_id || "",
        part_id: workOrder.part_id || "",
        plybook_id: workOrder.plybook_id || "",
        nfc_tag_uid: existingNfcTag?.tag_uid || "",
        scheduled_date: workOrder.scheduled_date || "",
      });
    }
  }, [open, workOrder, existingNfcTag]);

  // Listen for NFC scans from USB reader
  useEffect(() => {
    if (isScanning && nfcBridge.lastRead) {
      setFormData((prev) => ({
        ...prev,
        nfc_tag_uid: nfcBridge.lastRead?.uid || "",
      }));
      setIsScanning(false);
      toast.success(`NFC detectado: ${nfcBridge.lastRead?.uid}`);
    }
  }, [nfcBridge.lastRead, isScanning]);

  const handleSave = async () => {
    if (!formData.product_name.trim()) {
      toast.error("El nombre del producto es requerido");
      return;
    }

    setLoading(true);
    try {
      const currentUser = await getCurrentUser();

      // 1. Update work order
      const updatePayload: Record<string, any> = {
        product_name: formData.product_name.trim(),
        customer: formData.customer.trim() || null,
        quantity: formData.quantity,
        priority: formData.priority,
        project_id: formData.project_id || null,
        custom_project_name: formData.custom_project_name.trim() || null,
        mold_id: formData.mold_id || null,
        part_id: formData.part_id.trim() || null,
        plybook_id: formData.plybook_id || null,
        updated_at: new Date().toISOString(),
      };

      // Fecha de inicio: solo supervisor/admin pueden modificarla
      if (canEditStartDate) {
        updatePayload.scheduled_date = formData.scheduled_date || null;
        // Si se fija fecha a una OT en espera, entra programada en el planificador;
        // si se quita la fecha, vuelve a espera. No se toca en otros estados.
        if (formData.scheduled_date && (workOrder.planning_status === "standby" || !workOrder.planning_status)) {
          updatePayload.planning_status = "scheduled";
        } else if (!formData.scheduled_date && workOrder.planning_status === "scheduled") {
          updatePayload.planning_status = "standby";
        }
      }

      const { error: orderError } = await api
        .from("work_orders")
        .update(updatePayload)
        .eq("id", workOrder.id);

      if (orderError) throw orderError;

      // 2. Handle NFC tag linking
      if (formData.nfc_tag_uid.trim()) {
        const nfcUid = formData.nfc_tag_uid.trim().toUpperCase();

        // Check if this tag already exists
        const { data: existingTag } = await api
          .from("nfc_tags")
          .select("id, work_order_id")
          .eq("tag_uid", nfcUid)
          .maybeSingleRow();

        if (existingTag) {
          // Update existing tag to link to this work order
          const { error: updateError } = await api
            .from("nfc_tags")
            .update({
              work_order_id: workOrder.id,
              tag_type: "work_order",
              assigned_at: new Date().toISOString(),
              assigned_by: currentUser?.id,
            })
            .eq("id", existingTag.id);

          if (updateError) throw updateError;
        } else {
          // Create new tag
          const { error: insertError } = await api
            .from("nfc_tags")
            .insert({
              tag_uid: nfcUid,
              tag_label: `OT-${workOrder.order_number}`,
              work_order_id: workOrder.id,
              tag_type: "work_order",
              status: "active",
              assigned_at: new Date().toISOString(),
              assigned_by: currentUser?.id,
            });

          if (insertError) throw insertError;
        }
      } else if (existingNfcTag?.tag_uid) {
        // Remove NFC link if cleared
        const { error: unlinkError } = await api
          .from("nfc_tags")
          .update({
            work_order_id: null,
            assigned_at: null,
            assigned_by: null,
          })
          .eq("work_order_id", workOrder.id);

        if (unlinkError) throw unlinkError;
      }

      toast.success("Orden de trabajo actualizada");
      queryClient.invalidateQueries({ queryKey: ["work-order", workOrder.id] });
      queryClient.invalidateQueries({ queryKey: ["work-orders"] });
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || "Error al actualizar la orden");
    } finally {
      setLoading(false);
    }
  };

  const handleScanNfc = () => {
    if (!nfcBridge.isReaderReady) {
      toast.error("Lector NFC no conectado");
      return;
    }
    setIsScanning(true);
    toast.info("Acerca el tag NFC al lector...");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Editar Orden: {workOrder?.order_number}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Product Name */}
          <div className="space-y-2">
            <Label>Nombre del Producto *</Label>
            <Input
              value={formData.product_name}
              onChange={(e) =>
                setFormData({ ...formData, product_name: e.target.value })
              }
              placeholder="Nombre del producto"
            />
          </div>

          {/* Customer */}
          <div className="space-y-2">
            <Label>Cliente</Label>
            <Select
              value={formData.customer || "__none__"}
              onValueChange={(value) =>
                setFormData({
                  ...formData,
                  customer: value === "__none__" ? "" : value,
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar cliente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sin cliente</SelectItem>
                {customers?.filter(c => c.name).map((customer) => (
                  <SelectItem key={customer.id} value={customer.name}>
                    {customer.name} {customer.code && `(${customer.code})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fecha de inicio (solo editable por supervisor/admin) */}
          <div className="space-y-2">
            <Label>Fecha de inicio</Label>
            <Input
              type="date"
              value={formData.scheduled_date}
              onChange={(e) =>
                setFormData({ ...formData, scheduled_date: e.target.value })
              }
              disabled={!canEditStartDate}
            />
            <p className="text-xs text-muted-foreground">
              {canEditStartDate
                ? "La OT se carga en producción para esta fecha"
                : "Solo supervisor o administrador pueden modificar la fecha de inicio"}
            </p>
          </div>

          {/* Quantity and Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Cantidad</Label>
              <Input
                type="number"
                value={formData.quantity}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    quantity: parseInt(e.target.value) || 1,
                  })
                }
                min={1}
              />
            </div>
            <div className="space-y-2">
              <Label>Prioridad</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) =>
                  setFormData({ ...formData, priority: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Baja</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="urgent">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Project */}
          <div className="space-y-2">
            <Label>Proyecto</Label>
            <Select
              value={formData.project_id || "__none__"}
              onValueChange={(value) =>
                setFormData({
                  ...formData,
                  project_id: value === "__none__" ? "" : value,
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar proyecto" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sin proyecto</SelectItem>
                {projects?.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name} {project.reference_code && `(${project.reference_code})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Mold */}
          <div className="space-y-2">
            <Label>Molde</Label>
            <Select
              value={formData.mold_id || "__none__"}
              onValueChange={(value) =>
                setFormData({
                  ...formData,
                  mold_id: value === "__none__" ? "" : value,
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar molde" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sin molde</SelectItem>
                {molds?.map((mold) => (
                  <SelectItem key={mold.id} value={mold.id}>
                    {mold.name} {mold.code && `(${mold.code})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Plybook */}
          <div className="space-y-2">
            <Label>Plybook / Hoja de Laminado</Label>
            <Select
              value={formData.plybook_id || "__none__"}
              onValueChange={(value) =>
                setFormData({
                  ...formData,
                  plybook_id: value === "__none__" ? "" : value,
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Asignar plybook (opcional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sin plybook</SelectItem>
                {plybooks?.filter((pb: any) => pb.id).map((pb: any) => (
                  <SelectItem key={pb.id} value={pb.id}>
                    {pb.name} {pb.code && `(${pb.code})`} - {pb.total_plies} capas {pb.status === 'draft' ? '[Borrador]' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Part ID */}
          <div className="space-y-2">
            <Label>ID de Pieza</Label>
            <Input
              value={formData.part_id}
              onChange={(e) =>
                setFormData({ ...formData, part_id: e.target.value })
              }
              placeholder="Identificador de pieza"
            />
          </div>

          {/* NFC Tag */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Nfc className="w-4 h-4" />
              Tag NFC
            </Label>
            <div className="flex gap-2">
              <Input
                value={formData.nfc_tag_uid}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    nfc_tag_uid: e.target.value.toUpperCase(),
                  })
                }
                placeholder="UID del tag NFC"
                className="flex-1 font-mono"
              />
              <Button
                type="button"
                variant={isScanning ? "default" : "outline"}
                size="icon"
                onClick={handleScanNfc}
                disabled={!nfcBridge.isReaderReady}
                title={
                  nfcBridge.isReaderReady
                    ? "Escanear con lector USB"
                    : "Lector USB no conectado"
                }
              >
                <ScanLine
                  className={`w-4 h-4 ${isScanning ? "animate-pulse" : ""}`}
                />
              </Button>
              {isMobileNfcSupported && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={scanningMobile || isMobileScanning}
                  onClick={async () => {
                    setScanningMobile(true);
                    const result = await scanTagUid();
                    setScanningMobile(false);
                    if (result?.serialNumber) {
                      setFormData((prev) => ({ ...prev, nfc_tag_uid: result.serialNumber.toUpperCase() }));
                      toast.success(`NFC móvil detectado: ${result.serialNumber}`);
                    }
                  }}
                  title="Escanear con NFC del móvil"
                >
                  <Nfc className={`w-4 h-4 ${scanningMobile ? "animate-pulse" : ""}`} />
                </Button>
              )}
            </div>
            {nfcBridge.isReaderReady ? (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <span className="w-2 h-2 bg-green-500 rounded-full" />
                Lector NFC conectado
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Ingresa el UID manualmente o conecta un lector NFC
              </p>
            )}
            {existingNfcTag && formData.nfc_tag_uid !== existingNfcTag.tag_uid && (
              <Badge variant="outline" className="text-xs">
                NFC actual: {existingNfcTag.tag_uid}
              </Badge>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Guardar Cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
